import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrTableFormComponent } from '@app/components/pr-form/pr-table-form/pr-table-form.component';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { EaSimulationScpiPackFormBuilder } from './ea-simulation-scpi-pack-form.builder';

@Component({
    selector: 'ea-simulation-scpi-pack-form',
    templateUrl: './ea-simulation-scpi-pack-form.component.html',
    styleUrls: ['./ea-simulation-scpi-pack-form.component.scss'],
    providers: [
        {
            provide: PrFormAbstract,
            useExisting: forwardRef(() => EaSimulationScpiPackFormComponent),
        },
    ],
    standalone: false
})
export class EaSimulationScpiPackFormComponent
  extends EaSimulationScpiPackFormBuilder
  implements AfterViewInit
{
  scpiList?;
  doneSubject: Subject<void> = new Subject<void>();

  @Output() onSubmitForm: EventEmitter<any> = new EventEmitter<{}>();
  @ViewChild('tableForm', { static: true }) tableForm: PrTableFormComponent;

  constructor(
    protected formConstructor: PrFormConstructor,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);
    this.referenceDao
      .getScpiSimulation()
      .subscribe(scpi => (this.scpiList = scpi));
  }

  // Colonnes additionnelles communes aux modes "Pourcentage" et "Parts" : permettent de
  // saisir, par SCPI, un versement programmé (montant + durée) et un taux de réinvestissement
  // des dividendes (% + durée). Les durées sont pré-remplies avec la durée du projet par
  // {@link EaSimulationScpiPackFormBuilder.createModel}.
  private isProgrammedDisabled = (row: any) =>
    row?.scpiPlanEpargneImmobilier !== true;
  private isReinvestmentDisabled = (row: any) =>
    row?.scpiReinvestissementDividendes !== true;

  private programmedAndReinvestmentColumns = [
    new PrTableColumn('Versement', 'programmedAmount', 'input', {
      changeCallback: this.tableSimpleNumericColumnChanged.bind(this),
      disabled: this.isProgrammedDisabled,
      suffix: '€/mois',
      span: 2,
    }),
    // Les durées sont saisies et affichées en années côté UI ; converties en mois dans
    // extract() avant l'envoi au backend.
    new PrTableColumn('Durée vers.', 'programmedDurationYears', 'input', {
      changeCallback: this.tableSimpleNumericColumnChanged.bind(this),
      disabled: this.isProgrammedDisabled,
      suffix: 'ans',
      span: 2,
    }),
    new PrTableColumn('Réinvest.', 'dividendReinvestmentRate', 'input', {
      changeCallback: this.tableSimpleNumericColumnChanged.bind(this),
      disabled: this.isReinvestmentDisabled,
      suffix: '%',
      span: 2,
    }),
    new PrTableColumn(
      'Durée réinv.',
      'dividendReinvestmentDurationYears',
      'input',
      {
        changeCallback: this.tableSimpleNumericColumnChanged.bind(this),
        disabled: this.isReinvestmentDisabled,
        suffix: 'ans',
        span: 2,
      },
    ),
  ];

  percentColumnsDefinition = [
    new PrTableColumn('Noms des SCPI', 'name', { span: 3, tooltip: true }),
    new PrTableColumn('Repartition', 'allocationPercentage', 'input', {
      changeCallback: this.tableAllocationColumnValueChanged.bind(this),
      suffix: '%',
      span: 2,
    }),
    new PrTableColumn('Parts', 'shareCount', 'text', { span: 2 }),
    new PrTableColumn('Montant investi', 'investedAmount', 'currency', {
      span: 2,
    }),
    ...this.programmedAndReinvestmentColumns,
    new PrTableColumn('', 'action', 'action'),
  ];

  shareColumnsDefinition = [
    new PrTableColumn('Noms des SCPI', 'name', { span: 3 }),
    new PrTableColumn('Repartition', 'allocationPercentage', 'percent', {
      span: 2,
      suffix: '%',
    }),
    new PrTableColumn('Parts', 'shareCount', 'input', {
      changeCallback: this.tablePartPriceColumnValueChanged.bind(this),
      span: 2,
    }),
    new PrTableColumn('Montant investi', 'investedAmount', 'currency', {
      span: 2,
    }),
    ...this.programmedAndReinvestmentColumns,
    new PrTableColumn('', 'action', 'action'),
  ];

  currentColumnDefinition = this.percentColumnsDefinition;

  ngAfterViewInit(): void {
    if (this.options.ownership == 'BARE_OWNERSHIP') {
      this.percentColumnsDefinition.splice(
        3,
        0,
        new PrTableColumn('Clé de démembrement', 'dismembermentKey', 'input', {
          changeCallback: this.tableDismembermentColumnValueChanged.bind(this),
          suffix: '%',
          span: 2,
        }),
      );
      this.shareColumnsDefinition.splice(
        3,
        0,
        new PrTableColumn('Clé de démembrement', 'dismembermentKey', 'input', {
          changeCallback: this.tableDismembermentColumnValueChanged.bind(this),
          suffix: '%',
          span: 2,
        }),
      );
      // Versements programmés et réinvestissement des dividendes ne sont pas autorisés en
      // démembrement (le backend rejette avec une 400). On retire les colonnes correspondantes
      // pour ne pas exposer un workflow non supporté.
      this.percentColumnsDefinition = this.percentColumnsDefinition.filter(
        column => !this.isProgrammedOrReinvestmentColumn(column),
      );
      this.shareColumnsDefinition = this.shareColumnsDefinition.filter(
        column => !this.isProgrammedOrReinvestmentColumn(column),
      );
      this.currentColumnDefinition = this.percentColumnsDefinition;
    }
  }

  private isProgrammedOrReinvestmentColumn(column: PrTableColumn): boolean {
    return [
      'programmedAmount',
      'programmedDurationYears',
      'dividendReinvestmentRate',
      'dividendReinvestmentDurationYears',
    ].includes(column.name as string);
  }

  tableAllocationColumnValueChanged(value: any, row: any, column: any) {
    this.scpis.controls[row.index].setValue({
      ...this.scpis.controls[row.index].value,
      allocationPercentage: value / 100,
      shareCount: row.partPrice
        ? Math.floor(
            ((value / 100) * this.options.investedAmount) /
              (row.partPrice *
                (row.dismembermentKey ? row.dismembermentKey : 1)),
          )
        : null,
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  tablePartPriceColumnValueChanged(value: any, row: any, column: any) {
    let scpi = this.scpis.controls[row.index];
    scpi.patchValue({ shareCount: +value });

    this.scpis.controls.map(scpi =>
      scpi.patchValue({
        investedAmount:
          scpi.value.shareCount *
          scpi.value.partPrice *
          (scpi.value.dismembermentKey || 1),
      }),
    );

    let totalInvestedAmount = this.scpis.controls.reduce(
      (acc, scpi) => (acc += scpi.value.investedAmount),
      0,
    );
    this.scpis.controls.map(scpi =>
      scpi.patchValue({
        allocationPercentage: scpi.value.investedAmount / totalInvestedAmount,
      }),
    );

    this.options.investedAmount = totalInvestedAmount;

    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  tableDismembermentColumnValueChanged(value: any, row: any, column: any) {
    this.scpis.controls[row.index].setValue({
      ...this.scpis.controls[row.index].value,
      [column.name]: value / 100,
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  /**
   * Callback générique pour les colonnes numériques simples (versement programmé, durées,
   * taux de réinvestissement). Patche la valeur sans transformation : la conversion %
   * → 0..1 du taux de réinvestissement se fait dans {@code extract()} du builder.
   */
  tableSimpleNumericColumnChanged(value: any, row: any, column: any) {
    const numeric = value === null || value === '' ? null : +value;
    this.scpis.controls[row.index].patchValue({ [column.name]: numeric });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  markAsDirtyTouchedAndRefresh(control) {
    this.scpis.controls[control].markAsDirty();
    this.scpis.controls[control].markAsTouched();
    this.tableForm.initTable();
  }

  formatTableDataFunction() {
    return data =>
      _(data)
        .map((data, i) => {
          return {
            index: i,
            name: data.name,
            allocationPercentage: this.floored(data.allocationPercentage * 100),
            shareCount: data.shareCount,
            dismembermentKey: data.dismembermentKey * 100,
            investedAmount:
              data.partPrice * (data.dismembermentKey || 1) * data.shareCount,
            partPrice: data.partPrice,
            // Colonnes versement programmé / réinvestissement.
            // Durées affichées en années (converties en mois dans extract()) ; taux
            // affiché en % (converti en 0..1 dans extract()).
            programmedAmount: data.programmedAmount,
            programmedDurationYears: data.programmedDurationYears,
            dividendReinvestmentRate: data.dividendReinvestmentRate,
            dividendReinvestmentDurationYears:
              data.dividendReinvestmentDurationYears,
            // Flags d'éligibilité exposés pour les `disabled` callbacks des colonnes.
            scpiReinvestissementDividendes: data.scpiReinvestissementDividendes,
            scpiPlanEpargneImmobilier: data.scpiPlanEpargneImmobilier,
          };
        })
        .value();
  }

  newOptionLogic() {
    //First element at 100%
    if (+this.scpis.controls.length == 1)
      return this.scpis.controls[0].setValue({
        ...this.scpis.controls[0].value,
        allocationPercentage: 1,
        shareCount: this.scpis.controls[0].value.partPrice
          ? Math.round(
              this.options.investedAmount /
                this.scpis.controls[0].value.partPrice,
            )
          : '',
      });

    // Form untouched = even allocation
    if (!this.scpis.dirty) {
      this.scpis.controls[0].setValue({
        ...this.scpis.controls[0].value,
        allocationPercentage: 0,
      });

      //Ignore first control in order to set the missing allocation percent inside
      _.forEach(this.scpis.controls, (c, i) =>
        +i != 0
          ? c.setValue({
              ...c.value,
              allocationPercentage: this.floored(
                1 / +this.scpis.controls.length,
              ),
              shareCount: c.value.partPrice
                ? Math.round(
                    (this.floored(1 / +this.scpis.controls.length) *
                      this.options.investedAmount) /
                      c.value.partPrice,
                  )
                : '',
            })
          : null,
      );

      let allocationLeft =
        this.totalAllocatedPercent < 1
          ? this.floored(1 - this.totalAllocatedPercent)
          : this.floored(this.totalAllocatedPercent - 1);
      this.scpis.controls[0].setValue({
        ...this.scpis.controls[0].value,
        allocationPercentage: allocationLeft,
        shareCount: this.scpis.controls[0].value.partPrice
          ? Math.round(
              (allocationLeft * this.options.investedAmount) /
                this.scpis.controls[0].value.partPrice,
            )
          : '',
      });
      return;
    }

    // Allocation < 1 = set value left to the control
    if (this.totalAllocatedPercent < 1) {
      let newControl = this.scpis.controls[this.scpis.value.length - 1];
      return newControl.setValue({
        ...newControl.value,
        allocationPercentage: 1 - this.totalAllocatedPercent,
        shareCount: this.scpis.controls[this.scpis.value.length - 1].value
          .partPrice
          ? Math.round(
              ((1 - this.totalAllocatedPercent / 100) *
                this.options.investedAmount) /
                this.scpis.controls[this.scpis.value.length - 1].value
                  .partPrice,
            )
          : '',
      });
    }
  }

  onSubmit() {
    this.derrors = true;

    // stop here if form is invalid
    if (this.form.invalid || this.totalAllocatedPercent != 1) {
      return;
    }

    this.onSubmitForm.emit();
    this.doneSubject.next();
    if (this.callback) {
      this.callback(this.extract());
    }
  }

  toggleColumnType(event) {
    this.currentColumnDefinition =
      event.id == 'PERCENT'
        ? this.percentColumnsDefinition
        : this.shareColumnsDefinition;
  }

  floored(value) {
    return Math.round(value * 100) / 100;
  }

  get scpis() {
    return this.form.get('scpis') as UntypedFormArray;
  }

  get totalAllocatedPercent() {
    return this.floored(_(this.scpis.value).map('allocationPercentage').sum());
  }

  get allocationPanel() {
    if (!this.scpis || !this.scpis.value || this.scpis.value.length == 0)
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez vos investissements',
      };
    else if (this.totalAllocatedPercent > 1)
      return {
        display: 'danger',
        icon: 'error',
        text: `${Math.round(
          this.totalAllocatedPercent * 100,
        )}% au total - Vous ne pouvez pas repartir plus de 100% de l'investissement`,
      };
    else if (this.totalAllocatedPercent < 1)
      return {
        display: 'warning',
        icon: 'warning',
        text: `Il reste ${Math.round(
          this.options.investedAmount * (1 - this.totalAllocatedPercent),
        )}€ de votre investissement à répartir, soit ${Math.round(
          (1 - this.totalAllocatedPercent) * 100,
        )}%`,
      };
    else if (
      _.filter(this.scpis.value, scpi => !scpi.allocationPercentage).length
    )
      return {
        display: 'danger',
        icon: 'error',
        text: 'Vous ne pouvez pas repartir 0% dans une SCPI',
      };
    else if (this.totalAllocatedPercent == 1)
      return {
        display: 'success',
        icon: 'check',
        text: 'Félicitations, vos investissements sont répartis à 100%',
      };
  }
}
